// @ts-strict-ignore
import pick from 'lodash/pick';

import {
  getClauseTrees,
  getLogicalOperators,
  isBehaviorOperandNode,
} from 'common/query-builder/cohorts-query-builder/util/expression-tree';

import {Behavior, BehaviorsDictionary} from '../behaviors/behavior';
import {BehaviorWindow} from '../behaviors/behavior-window';
import {Funnel} from '../behaviors/funnel';
import {FunnelStep} from '../behaviors/funnel-step';
import {FUNNEL_CLAUSE_OPERATOR} from '../constants';
import {FrequencyOperator} from '../enums/builder-enums';
import {ArithmeticOperator, BehaviorCountType, LogicalOperator, Property, WindowUnit} from '../enums/expression-enums';
import {OperatorNode} from '../expression/operator-node';
import {OperandNode} from '../expression/operand-node';
import {expressionToClauseRoot, expressionToLiteralRoot} from '../expression/utils';
import type {ClauseOperators, FrequencyClauseBuilderState, StagedBehaviorDateRange} from '../types/builder-state-types';
import type {EventFrequency} from '../types/expression-types';
import type {SerializedEventLiteral} from '../types/serialized-staged-cohort-types';
import {EventFrequencyClause} from './event-frequency-clause';
import {Literal} from './literal';

interface EventLiteralOptions {
  clauses: EventFrequencyClause[];
  labelPrefix: string;
  clauseOperators?: ClauseOperators;
}

export class EventLiteral extends Literal {
  public static deserialize(options: SerializedEventLiteral): EventLiteral {
    return new EventLiteral({
      clauseOperators: options.clauseOperators || null,
      clauses: options.clauses.map((c) => EventFrequencyClause.deserialize(c)),
      labelPrefix: options.labelPrefix,
    });
  }

  public static createDefaultWithPrefix(labelPrefix: string): EventLiteral {
    return new EventLiteral({
      clauseOperators: null,
      clauses: [EventFrequencyClause.createDefault()],
      labelPrefix,
    });
  }

  public static createFromParts({
    tree,
    behaviors,
    labelPrefix,
  }: {
    tree: OperatorNode;
    labelPrefix: string;
    behaviors: BehaviorsDictionary;
  }): EventLiteral {
    let clauseTrees = getClauseTrees(tree);
    if (!clauseTrees.length) {
      // cohorts not built by builder, but renderable in UI
      clauseTrees = [tree];
    }

    const behaviorKey = tree.find<string, OperandNode>((node) => node.operand.value as string, isBehaviorOperandNode);
    const funnel = behaviors[behaviorKey].funnel;
    let clauses: EventFrequencyClause[] = [];
    let clauseOperators: ClauseOperators = null;
    if (funnel) {
      clauseOperators = [FUNNEL_CLAUSE_OPERATOR, LogicalOperator.And];
      clauses = funnel.steps.map((step) => EventFrequencyClause.createFromFunnelStep(step, behaviors[behaviorKey]));
    } else {
      clauses = clauseTrees.map((t) => EventFrequencyClause.createFromParts({tree: t, behaviors}));
      if (clauses.length > 1) {
        const operators = getLogicalOperators(tree);
        clauseOperators = [operators[0], operators[1] || LogicalOperator.And];
      }
    }

    return new EventLiteral({
      clauseOperators,
      clauses,
      labelPrefix,
    });
  }

  public declare readonly clauses: EventFrequencyClause[];

  constructor(options: EventLiteralOptions) {
    super(options);
    this.clauses = options.clauses;
  }

  get behaviors(): Behavior[] {
    return this.clauses.map((clause) => clause.behavior);
  }

  get behaviorsDictionary(): BehaviorsDictionary {
    if (this.isFunnel) {
      const key = this.getBehaviorDictionaryKey(0);
      const funnelSteps = this.behaviors.map(
        (behavior, idx) => new FunnelStep(behavior.count, this.clauses[idx].frequency[0]),
      );
      const {fromDate, toDate, behaviorWindow} = this.behaviors[0];
      const funnelBehavior = new Behavior(null, new Funnel(funnelSteps), null, fromDate, toDate, behaviorWindow);
      return {[key]: funnelBehavior};
    }
    return this.behaviors.reduce((dict: BehaviorsDictionary, behavior: Behavior, idx: number) => {
      const key = this.getBehaviorDictionaryKey(idx);
      behavior.key = key;
      return Object.assign(dict, {[key]: behavior});
    }, {});
  }

  // @override
  get isFunnel(): boolean {
    return !!this.clauseOperators && this.clauseOperators[0] === FUNNEL_CLAUSE_OPERATOR;
  }

  public serialize(): SerializedEventLiteral {
    return {
      clauseOperators: this.clauseOperators,
      clauses: this.clauses.map((c) => c.serialize()),
      labelPrefix: this.labelPrefix,
    };
  }

  public addDefaultClause(): EventLiteral {
    return this.addClause<EventLiteral>(EventFrequencyClause.createDefault());
  }

  public getBehaviorDictionaryKey(idx: number): string {
    return `${this.labelPrefix}${idx}`;
  }

  public addPropertyFilter(clauseIdx: number): EventLiteral {
    return this.replaceClause<EventLiteral>(this.clauses[clauseIdx].addPropertyFilter(), clauseIdx);
  }

  public setClauseOperators(clauseOperators: ClauseOperators): EventLiteral {
    let newLiteral = this.newLiteral({clauseOperators});
    if (newLiteral.isFunnel && !this.isFunnel) {
      // cannot support distinct day, between operator, or negated first event with funnels, so we
      // ensure that each clause uses a single frequency value, absolute counts, and non-negated first event.
      newLiteral = newLiteral.clauses.reduce(
        (literal, _, idx) =>
          literal
            .setFrequencyOperator(FrequencyOperator.AtLeast, idx)
            .setBehaviorCountType(BehaviorCountType.Absolute, idx)
            .setClauseNegated(false, 0),
        newLiteral,
      );
    }

    return newLiteral;
  }

  public setClauseNegated(negated: boolean, clauseIdx: number): EventLiteral {
    return this.replaceClause<EventLiteral>(this.clauses[clauseIdx].setNegated(negated), clauseIdx);
  }

  public setBehaviorCountEvent(event: string, clauseIdx: number): EventLiteral {
    const behavior = this.getBehavior(clauseIdx).setEvent(event);
    return this.replaceClause<EventLiteral>(this.clauses[clauseIdx].setBehavior(behavior), clauseIdx);
  }

  public setBehaviorCountType(behaviorCountType: BehaviorCountType, clauseIdx: number): EventLiteral {
    const behavior = this.getBehavior(clauseIdx).setCountType(behaviorCountType);
    return this.replaceClause<EventLiteral>(this.clauses[clauseIdx].setBehavior(behavior), clauseIdx);
  }

  public setBehaviorCustomRange(unit: WindowUnit, value: number, clauseIdx: number): EventLiteral {
    const behaviorWindow = new BehaviorWindow(unit, value);
    const behavior = this.getBehavior(clauseIdx).setWindow(behaviorWindow);
    return this.replaceClause<EventLiteral>(this.clauses[clauseIdx].setBehavior(behavior), clauseIdx);
  }

  public setBehaviorDateRange(fromDate: string, toDate: string, clauseIdx: number): EventLiteral {
    const behavior = this.getBehavior(clauseIdx).setDateRange(fromDate, toDate);
    return this.replaceClause<EventLiteral>(this.clauses[clauseIdx].setBehavior(behavior), clauseIdx);
  }

  public setStagedBehaviorDateRange(options: StagedBehaviorDateRange, clauseIdx: number): EventLiteral {
    return this.replaceClause<EventLiteral>(this.clauses[clauseIdx].setStagedBehaviorDateRange(options), clauseIdx);
  }

  public setFrequencyOperator(frequencyOperator: FrequencyOperator, clauseIdx: number): EventLiteral {
    return this.replaceClause<EventLiteral>(this.clauses[clauseIdx].setFrequencyOperator(frequencyOperator), clauseIdx);
  }

  public setFrequencyState(builderState: FrequencyClauseBuilderState, clauseIdx: number): EventLiteral {
    return this.replaceClause<EventLiteral>(this.clauses[clauseIdx].setBuilderState(builderState), clauseIdx);
  }

  public setFrequencyValue(frequency: EventFrequency, clauseIdx: number): EventLiteral {
    return this.replaceClause<EventLiteral>(this.clauses[clauseIdx].setFrequency(frequency), clauseIdx);
  }

  // @override
  public getExpressionTree(): OperatorNode {
    let expressionTree: OperatorNode;
    if (this.isFunnel) {
      // for funnels, we consolidate all the clauses into funnel steps that are part of a single behavior
      // we always expect a clause root so we assign a clause root before assigning a literal root
      expressionTree = new OperatorNode(
        ArithmeticOperator.Equals,
        new OperandNode({property: Property.Behaviors, value: this.getBehaviorDictionaryKey(0)}),
        new OperandNode({property: Property.Literal, value: true}),
      );
      expressionTree = expressionToClauseRoot(expressionTree);
      expressionTree = expressionToLiteralRoot(expressionTree);
    } else {
      expressionTree = super.getExpressionTree();
    }

    return expressionTree;
  }

  public toTrackingData(): Obj {
    const hasClauseOperators = this.clauseOperators;
    return {
      'initial operator': hasClauseOperators ? this.clauseOperators[0] || `then` : null,
      'is event literal': true,
      'is funnel': this.isFunnel,
      'join operator': hasClauseOperators && !this.isFunnel ? this.clauseOperators[1] : null,
      'literal clauses': this.clauses.length,
    };
  }

  public sanitizeForDataGroup({dataGroupId, customEvents}): Literal {
    let newLiteralOptions: Partial<EventLiteralOptions> = {
      clauses: this.clauses.filter((clause) => !clause.isInvalidForDataGroup({dataGroupId, customEvents})),
    };
    if (newLiteralOptions.clauses?.length === 1) {
      newLiteralOptions = {...newLiteralOptions, ...{clauseOperators: null}};
    }
    return this.newLiteral(newLiteralOptions);
  }

  protected getOperatorLabelAtIndex(idx: number): string {
    const operatorIdx = Math.min(idx - 1, 1);
    return this.isFunnel ? `and then` : this.clauseOperators[operatorIdx];
  }

  // @override
  protected newLiteral(options: Partial<EventLiteralOptions>): EventLiteral {
    const oldOptions: EventLiteralOptions = pick(this, [`clauseOperators`, `clauses`, `labelPrefix`]);
    return new EventLiteral({...oldOptions, ...options});
  }

  private getBehavior(clauseIdx: number): Behavior {
    return this.clauses[clauseIdx].behavior;
  }
}
